import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../lib/api/client";
import { checkSession, sessionRetryDelay } from "../../lib/auth/session-check";
import { BareBand } from "../shell/bare-band";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";

function Screen({ children }: { children: React.ReactNode }) {
	return (
		<div className="flex min-h-dvh flex-col bg-paper">
			<BareBand />
			<main id="mses-main" className="mx-auto flex w-full max-w-[40rem] flex-col items-start gap-[1rem] px-[1.25rem] py-[3rem]">
				{children}
			</main>
		</div>
	);
}

/** From the first frame while `/auth/me` is in flight: the band and one calm line. */
export function SessionChecking() {
	const { t } = useTranslation();
	return (
		<Screen>
			<p role="status" className="flex items-center gap-[0.625rem] font-bold text-[1.125rem] text-ink">
				<Spinner className="size-[1.25rem] text-board" label={t("session.checking")} />
				{t("session.checking")}
			</p>
		</Screen>
	);
}

/**
 * The server did not answer the session check. Nothing is lost and nobody is
 * signed out: the screen retries on its own (2, 4, 8, 15, then every 30 s,
 * and at once when the browser says the network is back) and continues into
 * the app the moment `/auth/me` answers. A credential verdict on a retry is
 * handed back to the guard, which sends the operator to sign in.
 */
export function SessionUnreachable({ reset }: { reset: () => void }) {
	const { t } = useTranslation();
	const router = useRouter();
	const queryClient = useQueryClient();
	const [attempt, setAttempt] = useState(0);
	const [checking, setChecking] = useState(false);
	const [nextAt, setNextAt] = useState(() => Date.now() + sessionRetryDelay(0));
	const [now, setNow] = useState(() => Date.now());
	const inFlight = useRef(false);

	const tryNow = useCallback(async () => {
		if (inFlight.current) return;
		inFlight.current = true;
		setChecking(true);
		try {
			await checkSession(queryClient);
			reset();
			await router.invalidate();
		} catch (err) {
			if (err instanceof ApiError && err.transient) {
				setAttempt((n) => n + 1);
			} else {
				// The server ruled: let the guard decide (it redirects to /login).
				reset();
				await router.invalidate();
			}
		} finally {
			inFlight.current = false;
			setChecking(false);
		}
	}, [queryClient, reset, router]);

	// Schedule the next background try after each failure.
	useEffect(() => {
		const delay = sessionRetryDelay(attempt);
		setNextAt(Date.now() + delay);
		const id = window.setTimeout(() => void tryNow(), delay);
		return () => window.clearTimeout(id);
	}, [attempt, tryNow]);

	// The network is back: don't wait for the timer.
	useEffect(() => {
		const onOnline = () => void tryNow();
		window.addEventListener("online", onOnline);
		return () => window.removeEventListener("online", onOnline);
	}, [tryNow]);

	// A one-second tick for the countdown only.
	useEffect(() => {
		const id = window.setInterval(() => setNow(Date.now()), 1_000);
		return () => window.clearInterval(id);
	}, []);

	const seconds = Math.max(0, Math.ceil((nextAt - now) / 1_000));
	return (
		<Screen>
			<h1 className="font-black text-[1.75rem] text-ink">{t("routeError.unreachable")}</h1>
			<p className="text-muted">{t("session.unreachableBody")}</p>
			<p role="status" className="flex min-h-[1.5rem] items-center gap-[0.5rem] font-bold text-[0.9375rem] text-ink">
				{checking ? (
					<>
						<Spinner className="size-[1rem] text-board" label={t("session.checkingNow")} />
						{t("session.checkingNow")}
					</>
				) : (
					t("session.nextTry", { seconds })
				)}
			</p>
			<Button onClick={() => void tryNow()} loading={checking} disabled={checking}>
				{t("session.tryNow")}
			</Button>
		</Screen>
	);
}
